/**
 * Manages the functionality of the sidebar.
 */
import {HEADER_HEIGHT} from "../consts/app";

class SidebarManager {
    /**
     * Represents the SidebarManager class.
     * @constructor
     */
    constructor() {
        if (!SidebarManager.instance) {
            this.initialize();
            SidebarManager.instance = this;
        }

        return SidebarManager.instance;
    }

    /**
     * Initializes the sidebar component.
     */
    initialize() {
        this.isNotScrolling = true;
        this.navigatedId = null;
        this.navActiveTimeout = null;
        this.navScrollListener = null;
        this.navActiveNavigations = 0;
        this.navCounter = 0;
        this.navLastAnimationRequest = null;
        this.navCurrentNavigationId = null;
        this.onContentLoaded();
    }

    /**
     * Handles the content loaded event.
     */
    onContentLoaded() {
        this.sidebarMobileOverlay = document.getElementById('sidebar-mobile-overlay');
        this.aside = document.querySelector('aside');
        this.sidebarTextContainer = document.querySelector('.sidebar-container-text');
        this.aside.style.height = window.innerHeight - HEADER_HEIGHT;
        this.sidebarExpander = document.querySelector('.sidebar-expander');
        this.sidebarToggler = document.querySelector('.sidebar-toggler');
        this.sidebarLinks = document.querySelectorAll('.sidebar-link');
        this.isStaticMenu = this.getIsStaticMenu();
        this.adjustMaxHeight();
        this.registerEvents();
        this.handleHashChange(null);
    }

    /**
     * Adjusts the maximum height of the sidebar text container based on the window height.
     */
    adjustMaxHeight() {
        const headerHeight = HEADER_HEIGHT + 16;
        const footer = document.querySelector('footer');
        const footerHeight = window.innerWidth >= 992 ? this.visibleHeight(footer) : 0;
        const windowHeight = window.innerHeight;
        const maxHeight = windowHeight - headerHeight - footerHeight;
        if (this.sidebarTextContainer) {
            this.sidebarTextContainer.style.maxHeight = `${maxHeight}px`;
        }
        this.ensureVisibility(this.aside.querySelector('.sidebar-link.is-active'));
    }


    /**
     * Calculates the visible height of an element within the viewport.
     *
     * @param {Element} element - The element to calculate the visible height for.
     * @returns {number} The visible height of the element.
     */
    visibleHeight(element) {
        const rect = element.getBoundingClientRect();
        if (rect.top >= window.innerHeight) {
            return 0;
        } else if (rect.bottom < 0) {
            return 0;
        } else {
            const visibleTop = Math.max(rect.top, 0);
            const visibleBottom = Math.min(rect.bottom, window.innerHeight);
            return visibleBottom - visibleTop;
        }
    }

    /**
     * Determines if the sidebar menu is static.
     * @returns {boolean} True if no sidebar link has a 'data-id', otherwise false.
     */
    getIsStaticMenu() {
        for (let link of this.sidebarLinks) {
            if (link.hasAttribute('data-id')) {
                return false;
            }
        }
        return true;
    }

    /**
     * Registers event listeners for the sidebar.
     */
    registerEvents() {
        this.removeEventListeners();

        if (!this.isStaticMenu) {
            window.addEventListener('scroll', () => this.handleScroll());
            window.addEventListener('resize', () => this.handleScroll());
            window.addEventListener('vdRenavigateRequested', (event) => this.handleHashChange(event));
            window.addEventListener('vdNavigateTo', (event) => this.handleNavigateTo(event));
        }

        window.addEventListener('hashchange', (event) => this.handleHashChange(event));
        window.addEventListener('resize', () => this.adjustMaxHeight());
        window.addEventListener('scroll', () => this.adjustMaxHeight());

        document.querySelectorAll('.sidebar-link').forEach(link => {
            link.addEventListener('click', (event) => {
                this.handleLinkClick(event, link);
            });
        });

        this.sidebarToggler.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleSidebar();
        });

    }

    /**
     * Removes event listeners from the sidebar and other elements.
     */
    removeEventListeners() {
        if (!this.isStaticMenu) {
            window.removeEventListener('scroll', () => this.handleScroll());
            window.removeEventListener('resize', () => this.handleScroll());
            window.removeEventListener('vdRenavigateRequested', () => this.handleHashChange());
            window.removeEventListener('vdNavigateTo', () => this.handleNavigateTo());
        }

        window.removeEventListener('hashchange', () => this.handleHashChange());

        document.querySelectorAll('.sidebar-link').forEach(link => {
            link.removeEventListener('click', (event) => this.handleLinkClick(event, link));
        });

        if (this.sidebarToggler) {
            this.sidebarToggler.removeEventListener('click', (event) => {
                event.preventDefault();
                this.toggleSidebar();
            });
        }
    }

    /**
     * Activates a sidebar link and toggles its associated submenu's visibility.
     *
     * @param {HTMLElement} link - The link element to set as active.
     * @param {boolean} [isUserAction=false] - Indicates whether the toggle action is triggered by a user action.
     */
    setActiveLink(link, isUserAction = false) {
        if (!link.classList.contains('is-active')) {
            this.sidebarLinks.forEach(l => l.classList.remove('is-active'));
            link.classList.add('is-active');
            this.toggleOpen(link, true, isUserAction);
        }
        if (link.classList.contains('is-active')) {
            this.toggleOpen(link, true, isUserAction);
        }
    }

    /**
     * Manages 'is-active' class for sidebar links based on their associated <ul> visibility.
     * Adds 'is-active' to the external link (<a>) when the nested <ul> is hidden (has 'd-none').
     * Removes 'is-active' from the external link when the nested <ul> is visible, to shift focus to internal links.
     */
    correctActiveLink() {
        const activeLinks = this.aside.querySelectorAll('.sidebar-link.is-active');
        activeLinks.forEach(activeLink => {
            const parentUl = activeLink.closest('li').parentElement;
            const siblingLink = parentUl.previousElementSibling;

            if (siblingLink && siblingLink.classList.contains('sidebar-link')) {
                if (parentUl.classList.contains('d-none')) {
                    siblingLink.classList.add('is-active');
                } else {
                    if (siblingLink.classList.contains('is-active')) {
                        siblingLink.classList.remove('is-active');
                    }
                }
            }
        });
    }

    /**
     * Ensures that a given element is visible within the bounds of a sidebar ('aside').
     * This function adjusts the scrollbar position of the sidebar to bring the element into view,
     * especially useful when navigating to elements that might be out of the visible area.
     *
     * @param {HTMLElement} element - The element to make visible within the sidebar.
     */
    ensureVisibility(element) {

        if (!element) return;

        const rect = element.getBoundingClientRect();
        const asideRect = this.sidebarTextContainer.getBoundingClientRect();

        // Check if the element is outside the visible bounds of the aside
        if (rect.bottom > asideRect.bottom || rect.top < asideRect.top) {
            // Calculate the center position
            const centerPosition = rect.top - asideRect.top - (asideRect.height / 2) + (rect.height / 2);
            this.sidebarTextContainer.scrollTop += centerPosition;
        }
    }

    /**
     * Toggles the open state of a link element and its associated sub-menu.
     *
     * @param {HTMLElement} link - The link element to toggle.
     * @param {boolean} [force=false] - Forces the toggle action, regardless of the current state.
     * @param {boolean} [isUserAction=false] - Indicates whether the toggle action is triggered by a user action.
     */
    toggleOpen(link, force = false, isUserAction = false) {
        if (!link) { return; }
        let isOpen = link.classList.contains('open');
        const hasManualOpen = link.classList.contains('manual-open');
        const subMenu = link.nextElementSibling;

        if (force) {
            isOpen = false;
        }

        if (!isOpen) {
            link.classList.add('open');
            if (subMenu) {
                this.toggleDisplay(subMenu, 'd-block');
            }
            if (isUserAction) {
                link.classList.add('manual-open');
            }
            // keep also all parent menus opened
            const parentMenu = link.parentElement?.closest('.sidebar-sub-menu');
            if (parentMenu) {
                const toggleLink = parentMenu.previousElementSibling;
                if (toggleLink && !toggleLink.classList.contains('open')) {
                    this.toggleOpen(toggleLink, true, false);
                }
            }
        } else {
            link.classList.remove('open', 'manual-open');
            if (subMenu) {
                this.toggleDisplay(subMenu, 'd-none');
            }
        }
    }

    /**
     * Handles the click event on a sidebar link.
     *
     * @param {Event} event - The click event.
     * @param {HTMLElement} link - The clicked sidebar link.
     */
    handleLinkClick(event, link) {

        // Check for external sidebar links that should not trigger navigation
        const dataId = link.dataset.id;
        if (dataId && dataId.startsWith('#link')) {
            return; // regular link to external page
        }

        event.preventDefault();

        // Checks if the clicked sidebar menu link points to a different page within the sidebar and navigates if so.
        const linkHref = link.getAttribute('href');

        let linkUrl = null;
        const currentUrl = window.location.href.split('#')[0];

        const alreadyOpen = link.classList.contains('open');
        const isActive = link.classList.contains('is-active');
        const isPermanent = link.classList.contains('is-permanent');
        const hasChildren = link.classList.contains('has-children');

        if (isPermanent) {
            link = this.findSameLinkInChildren(link);
        }

        if (linkHref) {
            linkUrl = linkHref.split('#')[0];
            if (linkUrl && currentUrl !== linkUrl) {
                window.location.href = linkHref; // Navigate to the new URL if it's a different page
                return;
            }
        }

        let targetId = link.dataset?.id?.replace('#', '') || '';
        targetId = targetId.includes('section-top') ? '' : targetId;

        if (!targetId) {
            if (linkHref) {
                window.location.href = linkHref;
            }
            return;
        }

        // Sidebar toggler (do not close toggle if statis menu active and clicked menu is not link)
        if (this.aside.classList.contains('open') && !this.isStaticMenu && targetId) {
            this.toggleSidebar();
        }

        if (alreadyOpen && hasChildren && isActive) {
            // Sidebar menu to which the link belongs is manually opened and the section is active - only close the menu section and do not navigate.
            this.toggleOpen(link);
            return;
        }

        // This ensures that the URL reflects the current navigation position without reloading the page.
        if (window.location.hash !== '#' + targetId) {
            history.pushState(null, null, '#' + targetId);
        }

        if (this.isStaticMenu && !isPermanent) {
            if (!targetId) {
                this.toggleOpen(link);
                this.correctActiveLink();
            }

        } else {
            this.navigatedId = targetId;
            this.setActiveLink(link, true);
            this.navigate(targetId);
        }
    }


    /**
     * Handles the change in the hash of the window location.
     * @param {boolean} [reload=false] - Indicates whether to reload the window location after handling the hash change.
     */
    handleHashChange(event = null, reload = false) {

        if (event) {
            event.preventDefault();
        }

        let targetLink = null;

        if (this.isStaticMenu) {
            // If the menu is static, search for a sidebar link whose href matches the current URL
            // const href = window.location.href;
            targetLink = this.getCurrentActiveLink();
        } else {

            // Exit early if not scrolling and there's no event, or the event type is not 'vdRenavigateRequested'.
            // This check is bypassed when 'vdRenavigateRequested' event is present to handle swagger init re-navigation needs.
            if (this.isNotScrolling && event && event.type !== 'vdRenavigateRequested') {
                return;
            }

            if (reload) {
                window.location.reload();
            }

            const hash = window.location.hash;

            // If a hash exists, search for a sidebar link by hash
            if (hash) {
                targetLink = this.aside.querySelector(`.sidebar-link[data-id="${hash}"]`);
            }
            // If no link found by hash, try to find the active link set on the backend
            if (!targetLink) {
                targetLink = this.aside.querySelector('.sidebar-link.is-active');
            }
        }

        if (!targetLink) {
            return;
        }

        // Handle 'is-permanent' class by searching children with the same href if it exists
        if (targetLink.classList.contains('is-permanent')) {
            targetLink = this.findSameLinkInChildren(targetLink);
        }

        if (!event || (event && event.type !== 'vdRenavigateRequested')) {
            this.setActiveLink(targetLink);
        }

        if (!this.isStaticMenu) {
            const targetId = targetLink.dataset?.id || '';
            this.navigate(targetId);
        }
    }

    /**
     * Handles a custom navigation event by extracting the target ID from the event's details.
     * If a target ID is present, it calls the navigate function to initiate the navigation to the specified element.
     * Uses optional chaining to safely access the target ID from the event object.
     *
     * @param {Event} event - The custom event containing navigation details.
     */
    handleNavigateTo(event) {
        const targetId = event?.detail?.targetId;
        if (targetId) {
            this.navigate(targetId);
        }
    }

    /**
     * Navigates to a specified section within the application by scrolling to it. This method manages the sidebar
     * link highlighting, dispatches a 'vdNavigated' event to notify other components of navigation completion, and
     * ensures that previous navigations are properly terminated before a new one begins. This method also handles
     * the detection of scroll completion to reset navigation state.
     *
     * @param {string} targetId - The ID of the target section to navigate to, which may include a hash prefix.
     */
    navigate(targetId) {

        // Initialize navigation with a unique ID for tracking
        const currentNavigationId = ++this.navCounter;
        this.navCurrentNavigationId = currentNavigationId;

        // Cancel any ongoing navigation processes
        if (this.navActiveNavigations > 0) {
            this.navActiveNavigations--;
        }
        if (this.navActiveTimeout) {
            clearTimeout(this.navActiveTimeout);
        }
        if (this.navScrollListener) {
            window.removeEventListener('scroll', this.navScrollListener);
        }

        // Set the navigation state to active
        this.isNotScrolling = true;
        this.navActiveNavigations++;

        // Prepare target ID by removing hash prefix if present
        const cleanTargetId = targetId.replace('#', '');
        let lastScrollTime = performance.now();
        let scrollStarted = false;

        // Define a listener for scroll events
        const scrollListener = () => {
            if (!this.isNotScrolling || this.navCurrentNavigationId !== currentNavigationId) {
                return;
            }
            if (!scrollStarted) {
                scrollStarted = true;
                lastScrollTime = performance.now();

                this.dispatchNavigatedEvent(cleanTargetId);  // Dispatch the navigation event early in the scroll
            }
        };
        this.navScrollListener = scrollListener;

        // Check if the scroll has finished to reset the navigation state
        const checkScrollFinished = () => {
            if (!this.isNotScrolling || this.navCurrentNavigationId !== currentNavigationId) {
                return;
            }
            const now = performance.now();
            if (now - lastScrollTime > 1000) {
                this.navActiveNavigations--;
                if (this.navActiveNavigations <= 0) {
                    this.isNotScrolling = false;  // Reset the scrolling flag when all navigations are complete
                }
                window.removeEventListener('scroll', scrollListener);
                clearTimeout(this.navActiveTimeout);
            } else {
                this.navLastAnimationRequest = requestAnimationFrame(checkScrollFinished);
            }
        };

        // Start the navigation process
        window.addEventListener('scroll', scrollListener);
        this.navLastAnimationRequest = requestAnimationFrame(() => {
            this.scrollToSection(cleanTargetId);  // Perform the scroll to the specified section
            this.navLastAnimationRequest = requestAnimationFrame(checkScrollFinished);
        });

        // Set a fallback timeout to ensure navigation does not hang indefinitely
        this.navActiveTimeout = setTimeout(() => {
            if (this.navActiveNavigations > 0 && this.navCurrentNavigationId === currentNavigationId) {
                this.navActiveNavigations--;
                if (this.navActiveNavigations <= 0) {
                    this.isNotScrolling = false;
                }
                window.removeEventListener('scroll', scrollListener);
            }
        }, 3000);
    }

    /**
     * Dispatches the 'vdNavigated' event to inform the application of a completed navigation to a specific section.
     * This is crucial for components such as Swagger UI that load dynamically, as it allows them to update accordingly.
     * The event includes the ID of the navigated section, enabling other components to adjust their state or UI in response.
     *
     * @param {string} cleanTargetId - The sanitized ID of the target section, used as detail in the event.
     */
    dispatchNavigatedEvent(cleanTargetId) {
        const navigatedEvent = new CustomEvent('vdNavigated', { detail: { targetId: cleanTargetId } });
        window.dispatchEvent(navigatedEvent);
    }

    /**
     * Scrolls smoothly to the specified section of the page.
     *
     * @param {string} sectionId - The ID of the section to scroll to.
     */
    scrollToSection(sectionId) {
        let scrollTop = 0;

        if (!sectionId.includes('section-top')) {
            const section = document.getElementById(sectionId);
            if (section) {
                scrollTop = section.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT;
            }
        }

        window.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }


    /**
     * Handles the scroll event and updates the active link and open menus in the sidebar.
     *
     * @param {Event} [event=null] - The scroll event object.
     */
    handleScroll(event = null) {

        // Early exit if currently navigating and no specific navigated ID provided
        if (this.isNotScrolling && !this.navigatedId) return;
        // When no specific navigated ID is provided, determine which section is currently visible or closest above the viewport
        const currentSectionId = this.navigatedId || this.getCurrentSectionId();
        // Dynamically highlight the menu link in the sidebar based on currentSectionId and open the parent menu sections in the sidebar.
        this.updateActiveLinkAndOpenMenus(currentSectionId);
        // Handles closing already inactive menu sections in the sidebar.
        this.closeInactiveMenus();

        this.navigatedId = null;
    }

    /**
     * Retrieves the ID of the current section based on its visibility within the viewport.
     * If no section is visible within the viewport, it selects the nearest section above the viewport.
     * @returns {string|null} The ID of the current section or the nearest section above the viewport.
     */
    getCurrentSectionId() {

        let currentSectionId = null;
        let smallestDistance = Infinity;
        let nearestAboveViewport = null;
        let nearestAboveDistance = Infinity;

        // Set currentSectionId by visibility within the viewport
        document.querySelectorAll('.sidebar-section').forEach(section => {
            const rect = section.getBoundingClientRect();
            const sectionTop = rect.top;
            const offset = - HEADER_HEIGHT;

            // Checks if a section is visible within the viewport bounds considering the offset
            if (sectionTop >= -offset && sectionTop < (window.innerHeight - offset)) {
                if (sectionTop < smallestDistance) {
                    smallestDistance = sectionTop;
                    currentSectionId = section.id;
                }
            }
            // Finds the closest section above the viewport if no sections are visible within it
            else if (sectionTop < -offset) {
                const distanceAbove = -sectionTop;
                if (distanceAbove < nearestAboveDistance) {
                    nearestAboveDistance = distanceAbove;
                    nearestAboveViewport = section.id;
                }
            }
        });

        // Selects the nearest section above the viewport if no section is within the viewport
        return currentSectionId || nearestAboveViewport;
    }

    /**
     * Updates the active link and opens the corresponding menus based on the current section ID.
     *
     * @param {string} currentSectionId - The ID of the current section.
     */
    updateActiveLinkAndOpenMenus(currentSectionId) {
        if (!currentSectionId) return;

        const link = document.querySelector(`.sidebar-link[data-id="#${currentSectionId}"]`);
        if (link && !link.classList.contains('is-active')) {
            this.setActiveLink(link, this.navigatedId != null);
        }
    }


    /**
     * Closes inactive menus.
     * Iterates over all elements that have children and are marked as open.
     * If the menu is open, not manually opened, does not contain an active link, and does not have any descendants with 'manual-open',
     * it closes the menu by removing the 'open' class from the link and hiding the submenu.
     */
    closeInactiveMenus() {
        // Iterates over all elements that have children and are marked as open.
        document.querySelectorAll('.li-has-children').forEach(menu => {
            const link = menu.querySelector('.sidebar-link');
            const isOpen = link.classList.contains('open');
            const isActiveLinkPresent = menu.querySelector('.sidebar-link.is-active') !== null;
            const isManualOpen = link.classList.contains('manual-open');
            const subMenu = link.nextElementSibling;

            // Check if there is any descendant with 'manual-open'.
            const hasManualOpenDescendant = Array.from(menu.querySelectorAll('.sidebar-link'))
                .some(subLink => subLink.classList.contains('manual-open'));

            // If the menu is open, not manually opened, does not contain an active link, and does not have any descendants with 'manual-open'.
            if (isOpen && !isManualOpen && !isActiveLinkPresent && !hasManualOpenDescendant) {
                // Close the menu.
                link.classList.remove('open');
                this.toggleDisplay(subMenu, 'd-none');
            }
        });
    }


    /**
     * Toggles the display of an element by adding or removing the 'd-block' and 'd-none' classes.
     * If a forceClass is provided, it will be used to set the display class directly.
     *
     * @param {HTMLElement} element - The element to toggle the display of.
     * @param {?string} [forceClass=null] - The class to force the element to have ('d-block' or 'd-none').
     */
    toggleDisplay(element, forceClass = null) {
        if (!element) return;

        if (forceClass === 'd-block') {
            element.classList.add('d-block');
            element.classList.remove('d-none');
            element.style.display = '';
        } else if (forceClass === 'd-none') {
            element.classList.add('d-none');
            element.classList.remove('d-block');
            element.style.display = '';
        } else {
            if (element.classList.contains('d-none')) {
                element.classList.remove('d-none');
                element.classList.add('d-block');
                element.style.display = '';
            } else {
                element.classList.remove('d-block');
                element.classList.add('d-none');
                element.style.display = '';
            }
        }
    }

    /**
     * Toggles the sidebar between open and closed states.
     */
    toggleSidebar() {
        if (this.aside.classList.contains('open')) {
            this.sidebarMobileOverlay.classList.add('d-none');
            this.aside.classList.remove('open');
            this.sidebarExpander.classList.replace('sidebar-collapse', 'sidebar-expand');
        } else {
            this.aside.classList.add('open');
            this.sidebarExpander.classList.replace('sidebar-expand', 'sidebar-collapse');
            this.sidebarMobileOverlay.classList.remove('d-none');
        }
    }



    /**
     * Finds and returns the currently active sidebar link based on the current URL.
     * It first checks against the main 'href' of each link and then against 'data-additional-hrefs'.
     *
     * @return {HTMLElement|null} The active sidebar link element or null if no active link is found.
     */
    getCurrentActiveLink() {
        const currentUrl = window.location.href;
        const normalizedCurrentUrl = this.normalizeUrl(currentUrl);

        for (let link of this.sidebarLinks) {
            // Check if the main href matches the current URL.
            const mainHref = link.getAttribute('href');
            const normalizedMainHref = this.normalizeUrl(mainHref);
            if (normalizedMainHref === normalizedCurrentUrl) {
                return link;
            }

            // Check additional hrefs if the main href doesn't match.
            const additionalHrefs = link.dataset.additionalHrefs;
            if (additionalHrefs) {
                const hrefs = additionalHrefs.split(';').map(href => this.normalizeUrl(href));
                if (hrefs.includes(normalizedCurrentUrl)) {
                    return link;
                }
            }
        }

        return null; // No active link found.
    }

    /**
     * Finds a child link with the same href as the target link among its sibling <ul> children.
     * Assumes the target link's immediate sibling is a <ul> element with class 'sidebar-sub-menu'.
     *
     * @param {Element} targetLink - The anchor element to match hrefs against.
     * @returns {Element} The matching child link, or the original targetLink if no match is found.
     */
    findSameLinkInChildren(targetLink) {
        const siblingUl = targetLink.nextElementSibling;
        if (siblingUl && siblingUl.classList.contains('sidebar-sub-menu')) {
            let foundLink = null;
            siblingUl.querySelectorAll('a').forEach(childLink => {
                if (childLink.href === targetLink.href) {
                    foundLink = childLink;
                }
            });
            return foundLink || targetLink;
        }
        return targetlink;
    }

    /**
     * Normalizes a URL to facilitate comparison by removing protocol, www, and trailing slashes.
     *
     * @param {string} url - The URL to normalize.
     * @return {string} The normalized URL.
     */
    normalizeUrl(url) {
        return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    }

}

document.addEventListener('DOMContentLoaded', () => {
    /**
     * Represents the sidebar aside element.
     * @type {HTMLElement}
     */
    const sidebarAside = document.getElementById('sidebar-aside');
    if (sidebarAside) {
        new SidebarManager();
    }
});
